import {ArrowDownIcon, ArrowUpIcon} from "lucide-react";

import {DataSourceFootnote} from "./DataSourceFootnote";

import type {XeroPnLOpexHighlight} from "@hooks/use-vnext";

interface XeroPnlOpexHighlightsProps {
    opex: XeroPnLOpexHighlight[];
    generatedAt?: string;
    showFootnote?: boolean;
}

function fmtCurrency(v: number): string {
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
    return `$${v.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
}

export function XeroPnlOpexHighlights({opex, generatedAt, showFootnote = true}: XeroPnlOpexHighlightsProps) {
    if (!opex.length) return null;

    return (
        <div>
            <h3 className="text-sm font-bold uppercase tracking-[2px] text-[#209DB3] mb-3">
                Operating Expenses — Top Accounts
            </h3>
            <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="bg-[#209DB3]/10 border-b">
                            <th className="text-left px-3 py-2 font-medium text-[#209DB3]">Account</th>
                            <th className="text-right px-3 py-2 font-medium text-[#209DB3]">Total</th>
                            <th className="text-right px-3 py-2 font-medium text-[#209DB3]">% of OpEx</th>
                            <th className="text-right px-3 py-2 font-medium text-[#209DB3]">MoM Change</th>
                        </tr>
                    </thead>
                    <tbody>
                        {opex.map((item) => (
                            <tr key={item.account_id} className="border-b border-gray-100 hover:bg-gray-50/50">
                                <td className="px-3 py-1.5 truncate max-w-[200px]" title={item.account_name}>
                                    {item.account_name}
                                </td>
                                <td className="text-right px-3 py-1.5 tabular-nums font-medium">
                                    {fmtCurrency(item.total)}
                                </td>
                                <td className="text-right px-3 py-1.5 tabular-nums">
                                    {item.pct_of_opex}%
                                </td>
                                <td className="text-right px-3 py-1.5">
                                    {item.mom_delta_pct != null ? (
                                        <span className={`inline-flex items-center gap-0.5 ${
                                            item.mom_delta_pct > 10 ? "text-red-600" :
                                            item.mom_delta_pct < -10 ? "text-green-600" :
                                            "text-muted-foreground"
                                        }`}>
                                            {item.mom_delta_pct > 0 && <ArrowUpIcon className="size-3" />}
                                            {item.mom_delta_pct < 0 && <ArrowDownIcon className="size-3" />}
                                            {item.mom_delta_pct > 0 ? "+" : ""}{item.mom_delta_pct}%
                                        </span>
                                    ) : (
                                        <span className="text-muted-foreground">{"\u2014"}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {showFootnote && <DataSourceFootnote generatedAt={generatedAt} />}
        </div>
    );
}
